import { countLabel } from "@/lib/plural";
import { absoluteUrl, SITE_ORIGIN } from "@/lib/canonical";
import { defaultSocialImageMeta } from "@/lib/og-image";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { AdBanner } from "@/components/AdBanner";
import { VideoGrid } from "@/components/VideoGrid";
import { SuggestVideoBlock } from "@/components/SuggestVideoBlock";
import { listCatalogPage } from "@/lib/catalog.functions";
import { themes } from "@/data/reportages";
import { breadcrumbSchema, videoItemListSchema } from "@/lib/schema";
import { MUSIC_FORMAT_LABELS, MUSIC_THEME, type MusicFormat } from "@/lib/music-format";

type VideosSearch = {
  q?: string;
  theme?: string;
  sort?: string;
  n?: number;
  f?: MusicFormat;
  page?: number;
};

const SORTS = [
  { value: "catalogue", label: "Catalogue" },
  { value: "duree-desc", label: "Plus longs" },
  { value: "duree-asc", label: "Plus courts" },
  { value: "titre", label: "A → Z" },
] as const;

const PAGE = 24;
const MAX = 120;

type PageDeps = {
  q?: string;
  theme?: string;
  sort?: string;
  f?: MusicFormat;
  n: number;
  page?: number;
};

const catalogQuery = (deps: PageDeps) => {
  // Mode paginé (?page=N) : tranche fixe de 24 pour l'indexation Google.
  // Sinon scroll infini : fenêtre élargie jusqu'à MAX.
  const paged = typeof deps.page === "number" && deps.page > 1;
  return queryOptions({
    queryKey: ["catalog", deps],
    queryFn: () =>
      listCatalogPage({
        data: {
          ...(deps.q ? { q: deps.q } : {}),
          ...(deps.theme ? { theme: deps.theme } : {}),
          ...(deps.sort ? { sort: deps.sort as "titre" } : {}),
          ...(deps.f ? { format: deps.f } : {}),
          offset: paged ? (deps.page! - 1) * PAGE : 0,
          limit: paged ? PAGE : Math.min(MAX, deps.n),
        },
      }),
    staleTime: 5 * 60 * 1000,
  });
};

export const Route = createFileRoute("/videos")({
  validateSearch: (search: Record<string, unknown>): VideosSearch => {
    const out: VideosSearch = {};
    if (typeof search.q === "string" && search.q.trim()) out.q = search.q.slice(0, 100);
    if (typeof search.theme === "string" && search.theme !== "all") out.theme = search.theme;
    if (typeof search.sort === "string" && search.sort !== "catalogue") out.sort = search.sort;
    const n = Number(search.n);
    if (Number.isFinite(n) && n > PAGE) out.n = Math.min(MAX, Math.round(n));
    const page = Number(search.page);
    if (Number.isFinite(page) && page > 1) out.page = Math.round(page);
    if (search.f === "doc" || search.f === "live") out.f = search.f;
    return out;
  },
  loaderDeps: ({ search }): PageDeps => ({
    ...(search.q ? { q: search.q } : {}),
    ...(search.theme ? { theme: search.theme } : {}),
    ...(search.sort ? { sort: search.sort } : {}),
    ...(search.f ? { f: search.f } : {}),
    n: Math.min(120, search.n ?? PAGE),
    ...(search.page && search.page > 1 ? { page: search.page } : {}),
  }),
  loader: ({ context, deps }) => context.queryClient.ensureQueryData(catalogQuery(deps)),
  head: (ctx: {
    match?: { search?: VideosSearch };
    loaderData?: {
      catalogTotal?: number;
      items?: Array<Record<string, unknown>>;
      meta?: Record<string, { uploadDate?: string; seconds?: number }>;
    };
  }) => {
    const s = ctx?.match?.search ?? {};
    const page = typeof s.page === "number" && s.page > 1 ? s.page : 1;
    const paged = page > 1;
    const filtered = Boolean(s.q || s.theme || s.sort || s.n || s.f);
    const total = ctx?.loaderData?.catalogTotal ?? 4142;
    const pageCount = Math.max(1, Math.ceil(total / PAGE));
    const items = (ctx?.loaderData?.items ?? []) as Parameters<typeof videoItemListSchema>[1];
    const itemMeta = ctx?.loaderData?.meta;

    // canonicalPath() supprime les query strings par design : pour les pages
    // paginées, la canonical doit rester auto-référente avec ?page=N.
    const canonical = paged ? `${SITE_ORIGIN}/videos?page=${page}` : absoluteUrl("/videos");
    const baseTitle = `${countLabel(total, "reportage")} sur la Corse à voir en ligne`;
    const title = paged ? `${baseTitle} — page ${page}` : baseTitle;
    return {
      meta: [
        // Les pages filtrées restent exclues de l'index ; les pages paginées s'indexent.
        { name: "robots", content: filtered ? "noindex, follow" : "index, follow" },
        { title },
        {
          name: "description",
          content: paged
            ? `Page ${page} du catalogue des reportages sur la Corse : documentaires et enquêtes visibles gratuitement en intégralité.`
            : "Le catalogue complet des documentaires et reportages sur la Corse visibles gratuitement en intégralité : mafia, GR20, histoire, terroir, société.",
        },
        { property: "og:title", content: paged ? `${title}` : `${countLabel(total, "reportage")} sur la Corse en accès libre` },
        {
          property: "og:description",
          content:
            "Tous les documentaires sur la Corse regardables gratuitement, classés par thématique.",
        },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
        ...defaultSocialImageMeta("Catalogue des reportages sur la Corse"),
      ],
      links: [
        { rel: "canonical", href: canonical },
        // Pagination crawlable : Google suit prev/next pour explorer toute la série.
        ...(paged
          ? [{ rel: "prev", href: page === 2 ? absoluteUrl("/videos") : `${SITE_ORIGIN}/videos?page=${page - 1}` }]
          : []),
        ...(page < pageCount
          ? [{ rel: "next", href: `${SITE_ORIGIN}/videos?page=${page + 1}` }]
          : []),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            videoItemListSchema(
              paged
                ? `Reportages sur la Corse en accès libre — page ${page}`
                : "Reportages sur la Corse en accès libre",
              items,
              {
                id: `${canonical}#itemlist`,
                url: canonical,
                offset: (page - 1) * PAGE,
                total,
                ...(itemMeta ? { meta: itemMeta } : {}),

              },

            ),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbSchema(
              paged
                ? [
                    { name: "Accueil", path: "/" },
                    { name: "Reportages en ligne", path: "/videos" },
                    { name: `Page ${page}`, path: `/videos?page=${page}` },
                  ]
                : [
                    { name: "Accueil", path: "/" },
                    { name: "Reportages en ligne", path: "/videos" },
                  ],
            ),
          ),
        },
      ],
    };
  },
  errorComponent: ({ error }) => (
    <div role="alert" className="mx-auto max-w-2xl px-5 py-24 text-center text-sm">
      Le catalogue n&apos;a pas pu être chargé. {error instanceof Error ? error.message : "Veuillez réessayer."}
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-5 py-24 text-center text-sm">
      Aucun reportage trouvé.
    </div>
  ),
  pendingMs: 300,
  component: VideosPage,
});

function VideosPage() {
  const search = Route.useSearch();
  const deps = Route.useLoaderDeps();
  const navigate = useNavigate({ from: "/videos" });
  const query = search.q ?? "";
  const theme = search.theme ?? "all";
  const sort = search.sort ?? "catalogue";
  const format = search.f;
  const page = search.page ?? 1;
  const paged = page > 1;
  const showFormats = theme === MUSIC_THEME;

  const { data } = useSuspenseQuery(catalogQuery(deps));
  const results = data.items;
  const total = data.total;
  const visible = results.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE));
  const hasMore = !paged && visible < total && visible < MAX;

  const setSearch = useCallback(
    (next: VideosSearch, replace = true) => {
      navigate({ search: (prev) => ({ ...prev, ...next }), replace, resetScroll: false });
    },
    [navigate],
  );

  // Scroll infini : on demande une fenêtre plus large au serveur.
  const sentinel = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setSearch({ n: Math.min(MAX, visible + PAGE) });
        }
      },
      { rootMargin: "600px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, visible, setSearch]);

  return (
    <div className="min-h-screen">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-5 py-12">
        <nav className="text-[0.65rem] uppercase tracking-[0.25em] text-muted-foreground">
          <Link to="/" className="hover:text-foreground">
            Accueil
          </Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">Reportages en ligne</span>
        </nav>

        <h1 className="mt-6 font-[family-name:var(--font-display)] text-4xl leading-tight md:text-6xl">
          {countLabel(data.catalogTotal, "reportage", "reportages")} sur la Corse à voir en intégralité
          {paged && (
            <span className="mt-2 block text-xl text-muted-foreground md:text-2xl">
              Page {page} sur {pageCount}
            </span>
          )}
        </h1>
        <p className="mt-5 max-w-2xl text-muted-foreground">
          Le catalogue complet des documentaires consacrés à l&apos;île,
          regardables gratuitement depuis le site et classés par thématique.
        </p>

        {/* Barre de filtres condensée et collante */}
        <div className="sticky top-16 z-30 -mx-5 mt-8 border-b border-border/60 bg-background/85 px-5 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/70">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <input
              type="search"
              defaultValue={query}
              onChange={(e) =>
                setSearch({ q: e.target.value || undefined, n: undefined, page: undefined })
              }
              placeholder="Rechercher un titre, une chaîne…"
              aria-label="Rechercher un reportage"
              className="h-10 w-full rounded-full border border-border bg-card px-5 text-sm outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ring md:w-72"
            />
            <div className="min-w-0 flex-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="flex w-max gap-2 text-xs">
                {[{ slug: "all", name: "Tout" }, ...themes].map((t) => (
                  <button
                    key={t.slug}
                    onClick={() =>
                      setSearch({
                        theme: t.slug === "all" ? undefined : t.slug,
                        n: undefined, page: undefined })
                    }
                    className={`inline-flex min-h-[40px] items-center whitespace-nowrap rounded-full border px-4 font-medium transition-colors ${
                      theme === t.slug
                        ? "border-foreground bg-foreground text-background"
                        : "border-border hover:bg-accent"
                    }`}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
            <label className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
              <span className="sr-only md:not-sr-only">Trier</span>
              <select
                value={sort}
                onChange={(e) =>
                  setSearch({
                    sort: e.target.value === "catalogue" ? undefined : e.target.value,
                    n: undefined, page: undefined })
                }
                aria-label="Trier les reportages"
                className="h-10 rounded-full border border-border bg-card px-4 text-xs font-medium text-foreground outline-none focus:ring-2 focus:ring-ring"
              >
                {SORTS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {showFormats && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-[0.6rem] uppercase tracking-[0.25em] text-muted-foreground">
                Format
              </span>
              {MUSIC_FORMAT_LABELS.map((f) => {
                const active = (format ?? "all") === f.value;
                return (
                  <button
                    key={f.value}
                    onClick={() =>
                      setSearch({
                        f: f.value === "all" ? undefined : (f.value as MusicFormat),
                        n: undefined, page: undefined })
                    }
                    className={`inline-flex min-h-[40px] items-center whitespace-nowrap rounded-full border px-4 font-medium transition-colors ${
                      active
                        ? "border-brand bg-brand/10 text-brand"
                        : "border-border hover:bg-accent"
                    }`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-8">
          <AdBanner format="leaderboard" />
        </div>

        <div className="mt-10">
          {total === 0 ? (
            <div className="rounded-2xl border border-border bg-card/50 px-6 py-14 text-center">
              <p className="font-[family-name:var(--font-display)] text-2xl">
                Aucun reportage pour cette recherche
              </p>
              <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
                Essayez un autre mot-clé, ou repartez du catalogue complet des{" "}
                {countLabel(data.catalogTotal, "reportage", "reportages")}.
              </p>
              <button
                onClick={() =>
                  setSearch({ q: undefined, theme: undefined, n: undefined, page: undefined })
                }
                className="mt-6 rounded-full bg-foreground px-6 py-2.5 text-xs font-medium text-background transition-opacity hover:opacity-90"
              >
                Réinitialiser les filtres
              </button>
            </div>
          ) : (
            <VideoGrid
              items={results}
              title={countLabel(total, "reportage")}
              intro="Cliquez sur une vignette pour lancer la lecture sans quitter la page."
            />
          )}
        </div>

        {paged ? (
          <nav
            aria-label="Pagination du catalogue"
            className="mt-12 flex flex-wrap items-center justify-center gap-2 text-xs"
          >
            {page > 1 && (
              <Link
                to="/videos"
                search={{ ...(page - 1 > 1 ? { page: page - 1 } : {}) }}
                className="inline-flex min-h-[40px] items-center rounded-full border border-border px-5 font-medium hover:bg-accent"
                rel="prev"
              >
                ← Page {page - 1}
              </Link>
            )}
            {/* Fenêtre de pages autour de la page courante */}
            {Array.from(
              { length: Math.min(7, pageCount) },
              (_, i) => Math.min(Math.max(1, page - 3), Math.max(1, pageCount - 6)) + i,
            )
              .filter((p, i, arr) => p <= pageCount && arr.indexOf(p) === i)
              .map((p) => (
                <Link
                  key={p}
                  to="/videos"
                  search={{ ...(p > 1 ? { page: p } : {}) }}
                  aria-current={p === page ? "page" : undefined}
                  className={`inline-flex min-h-[40px] min-w-[40px] items-center justify-center rounded-full border px-3 font-medium transition-colors ${
                    p === page
                      ? "border-foreground bg-foreground text-background"
                      : "border-border hover:bg-accent"
                  }`}
                >
                  {p}
                </Link>
              ))}
            {page < pageCount && (
              <Link
                to="/videos"
                search={{ page: page + 1 }}
                className="inline-flex min-h-[40px] items-center rounded-full border border-border px-5 font-medium hover:bg-accent"
                rel="next"
              >
                Page {page + 1} →
              </Link>
            )}
          </nav>
        ) : hasMore ? (
          <div ref={sentinel} className="mt-12 text-center">
            <button
              onClick={() => setSearch({ n: Math.min(MAX, visible + PAGE) })}
              className="rounded-full border border-border px-6 py-3 text-xs font-medium hover:bg-accent"
            >
              Charger plus de reportages
            </button>
            <p className="mt-3 text-xs text-muted-foreground">
              {visible} sur {countLabel(total, "reportage affiché", "reportages affichés")}
            </p>
            {/* Point d'entrée crawlable vers la série paginée */}
            <Link
              to="/videos"
              search={{ page: 2 }}
              className="mt-4 inline-block text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
            >
              Parcourir le catalogue page par page (page 2 sur {pageCount})
            </Link>
          </div>
        ) : total > 0 ? (
          <div className="mt-12 text-center">
            <p className="text-xs text-muted-foreground">
              Fin de la fenêtre affichée · {countLabel(total, "reportage", "reportages")} au catalogue
            </p>
            <Link
              to="/videos"
              search={{ page: 2 }}
              className="mt-4 inline-block text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
            >
              Continuer l&apos;exploration page par page (page 2 sur {pageCount})
            </Link>
          </div>
        ) : null}

        <SuggestVideoBlock context={query || undefined} />

        <div className="mt-16">
          <AdBanner format="leaderboard" />
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
